import type { ReactNode, SVGProps } from 'react';

export type IconProps = SVGProps<SVGSVGElement>;

// Shared geometry/prop contract for every foundation icon: 24x24 viewBox, stroke-only,
// currentColor (inherits the caller's text color — no hardcoded colors/pixel sizes/theme
// values). Decorative by default (aria-hidden + focusable="false") — interactive controls
// keep their own visible text/aria-label as the accessible name, never the SVG.
function Svg({ children, ...props }: IconProps & { children: ReactNode }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export function MenuIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </Svg>
  );
}

export function SunIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
      <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
    </Svg>
  );
}

export function MoonIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
    </Svg>
  );
}

export function ChevronUpIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <polyline points="18 15 12 9 6 15" />
    </Svg>
  );
}

export function ChevronDownIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <polyline points="6 9 12 15 18 9" />
    </Svg>
  );
}

export function XIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </Svg>
  );
}

export function CheckIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <polyline points="20 6 9 17 4 12" />
    </Svg>
  );
}

export function UploadIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 16V4" />
      <polyline points="6 9 12 3 18 9" />
      <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    </Svg>
  );
}

export function ArrowLeftIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </Svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Sidebar navigation icons (Tabler outline set, https://tabler.io/icons — MIT). Geometry copied
// as-is from the upstream outline SVGs so each one stays recognizable against its published
// design; only rendered through the shared `Svg` wrapper above (same viewBox/stroke/decorative
// contract as every other icon in this file — no per-icon style deviation).

export function AddressBookIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M20 6v12a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2" />
      <path d="M10 16h6" />
      <path d="M11 11a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
      <path d="M4 8h3" />
      <path d="M4 12h3" />
      <path d="M4 16h3" />
    </Svg>
  );
}

export function HourglassIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M6.5 7h11" />
      <path d="M6.5 17h11" />
      <path d="M6 20v-2a6 6 0 1 1 12 0v2a1 1 0 0 1 -1 1h-10a1 1 0 0 1 -1 -1" />
      <path d="M6 4v2a6 6 0 1 0 12 0v-2a1 1 0 0 0 -1 -1h-10a1 1 0 0 0 -1 1" />
    </Svg>
  );
}

export function ReceiptIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2m4 -14h6m-6 4h6m-2 4h2" />
    </Svg>
  );
}

export function CoinsIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 14c0 1.657 2.686 3 6 3s6 -1.343 6 -3s-2.686 -3 -6 -3s-6 1.343 -6 3" />
      <path d="M9 14v4c0 1.656 2.686 3 6 3s6 -1.344 6 -3v-4" />
      <path d="M3 6c0 1.072 1.144 2.062 3 2.598s4.144 .536 6 0c1.856 -.536 3 -1.526 3 -2.598c0 -1.072 -1.144 -2.062 -3 -2.598s-4.144 -.536 -6 0c-1.856 .536 -3 1.526 -3 2.598" />
      <path d="M3 6v10c0 .888 .772 1.45 2 2" />
      <path d="M3 11c0 .888 .772 1.45 2 2" />
    </Svg>
  );
}

export function BriefcaseIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M3 9a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2l0 -9" />
      <path d="M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2" />
      <path d="M12 12l0 .01" />
      <path d="M3 13a20 20 0 0 0 18 0" />
    </Svg>
  );
}

export function FlagIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M5 5a5 5 0 0 1 7 0a5 5 0 0 0 7 0v9a5 5 0 0 1 -7 0a5 5 0 0 0 -7 0v-9" />
      <path d="M5 21v-7" />
    </Svg>
  );
}

export function FileImportIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M14 3v4a1 1 0 0 0 1 1h4" />
      <path d="M5 13v-8a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2h-5.5m-9.5 -2h7m-3 -3l3 3l-3 3" />
    </Svg>
  );
}

export function FileTextIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M14 3v4a1 1 0 0 0 1 1h4" />
      <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2" />
      <path d="M9 9l1 0" />
      <path d="M9 13l6 0" />
      <path d="M9 17l6 0" />
    </Svg>
  );
}

export function BuildingIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M3 21l18 0" />
      <path d="M9 8l1 0" />
      <path d="M9 12l1 0" />
      <path d="M9 16l1 0" />
      <path d="M14 8l1 0" />
      <path d="M14 12l1 0" />
      <path d="M14 16l1 0" />
      <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16" />
    </Svg>
  );
}

export function UsersIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M5 7a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
      <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
    </Svg>
  );
}

export function ShieldLockIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3" />
      <path d="M11 11a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M12 12l0 2.5" />
    </Svg>
  );
}

export function ClipboardListIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
      <path d="M9 5a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2" />
      <path d="M9 12l.01 0" />
      <path d="M13 12l2 0" />
      <path d="M9 16l.01 0" />
      <path d="M13 16l2 0" />
    </Svg>
  );
}

export function CalendarIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12" />
      <path d="M16 3v4" />
      <path d="M8 3v4" />
      <path d="M4 11h16" />
      <path d="M11 15h1" />
      <path d="M12 15v3" />
    </Svg>
  );
}

export function ChartPieIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M10 3.2a9 9 0 1 0 10.8 10.8a1 1 0 0 0 -1 -1h-6.8a2 2 0 0 1 -2 -2v-7a.9 .9 0 0 0 -1 -.8" />
      <path d="M15 3.5a9 9 0 0 1 5.5 5.5h-4.5a1 1 0 0 1 -1 -1v-4.5" />
    </Svg>
  );
}

export function ChartBarIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M3 13a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -6" />
      <path d="M15 9a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -10" />
      <path d="M9 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -14" />
      <path d="M4 20h14" />
    </Svg>
  );
}

export function UserCheckIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
      <path d="M6 21v-2a4 4 0 0 1 4 -4h4" />
      <path d="M15 19l2 2l4 -4" />
    </Svg>
  );
}

export function ListCheckIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M3.5 5.5l1.5 1.5l2.5 -2.5" />
      <path d="M3.5 11.5l1.5 1.5l2.5 -2.5" />
      <path d="M3.5 17.5l1.5 1.5l2.5 -2.5" />
      <path d="M11 6l9 0" />
      <path d="M11 12l9 0" />
      <path d="M11 18l9 0" />
    </Svg>
  );
}

export function ActivityIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M3 12h4l3 8l4 -16l3 8h4" />
    </Svg>
  );
}

export function PencilIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
      <path d="M13.5 6.5l4 4" />
    </Svg>
  );
}

export function CalculatorIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 5a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2l0 -14" />
      <path d="M8 8a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1v1a1 1 0 0 1 -1 1h-6a1 1 0 0 1 -1 -1l0 -1" />
      <path d="M8 14l0 .01" />
      <path d="M12 14l0 .01" />
      <path d="M16 14l0 .01" />
      <path d="M8 17l0 .01" />
      <path d="M12 17l0 .01" />
      <path d="M16 17l0 .01" />
    </Svg>
  );
}

export function ReportMoneyIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
      <path d="M9 5a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2" />
      <path d="M14 11h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3h-2.5" />
      <path d="M12 17v1m0 -8v1" />
    </Svg>
  );
}

// Slice P3 — the opening-balances nav icon (Sidebar pins every icon unique). Balance scales:
// the literal glyph for "ნაშთი/balance", colliding with none of the money glyphs already in use
// (coins = advances, report-money = settlement, receipt = payments).
export function ScaleIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M7 20l10 0" />
      <path d="M6 6l6 -1l6 1" />
      <path d="M12 3l0 17" />
      <path d="M9 12l-3 -6l-3 6a3 3 0 0 0 6 0" />
      <path d="M21 12l-3 -6l-3 6a3 3 0 0 0 6 0" />
    </Svg>
  );
}

// Phase 7 Slice 7.6-UI — one more Sidebar nav icon (verified against the 21 added in 2dde620: no
// collision). Two stacked rows read as "a schedule of parts" — matches an installment template's
// own shape (a reusable list of line items) better than a document/checklist glyph already used
// elsewhere (file-text, clipboard-list, list-check).
export function LayoutListIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v2a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2l0 -2" />
      <path d="M4 16a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v2a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2l0 -2" />
    </Svg>
  );
}

// Phase 7 Slice 7.7-B — one more Sidebar nav icon (verified against the 22 already in use: no
// collision — the shared "clipboard" base is reused from ClipboardListIcon by Tabler's own
// clipboard-check/clipboard-list family design, but the content mark differs (checkmark vs.
// list dots) and the two live in different nav groups, so the full geometry signature is
// distinct). A checkmark-on-clipboard reads as "an agreement being tracked to completion" —
// matches a recovery plan's own shape (create → activate → track installments to paid) better
// than a generic document/list glyph already used elsewhere (file-text, list-check).
export function ClipboardCheckIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
      <path d="M9 5a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2" />
      <path d="M9 14l2 2l4 -4" />
    </Svg>
  );
}

// Phase 7 Slice 7.8-B — one more Sidebar nav icon (verified against the 23 already in use: no
// collision — a chain-link glyph is geometrically distinct from every clipboard/document/list
// icon already in the set). Two overlapping broken rings read as "one thing connected to
// another" — matches confirmed payment matching's own shape (a payment linked to a plan
// installment) better than a generic document/checklist glyph already used elsewhere.
export function LinkIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 15l6 -6" />
      <path d="M11 6l.463 -.536a5 5 0 0 1 7.071 7.072l-.534 .464" />
      <path d="M13 18l-.397 .534a5.068 5.068 0 0 1 -7.127 0a4.972 4.972 0 0 1 0 -7.071l.524 -.463" />
    </Svg>
  );
}

// Phase 7 Slice 7.11-B — one more Sidebar nav icon (verified against the 24 already in use: no
// collision — a bell is geometrically distinct from every clipboard/document/chain/list glyph
// already in the set). Matches the alerts/tasks page's own subject better than a generic
// clipboard/list glyph — the same bell shape the notification-family badges already imply
// (`AlertBadge`'s "შეტყობინებები" label) even though those badges render only a count bubble.
export function UserPlusIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="9" cy="7" r="4" />
      <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
      <path d="M16 11h6" />
      <path d="M19 8v6" />
    </Svg>
  );
}

export function LogoutIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M14 8v-2a2 2 0 0 0 -2 -2h-5a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h5a2 2 0 0 0 2 -2v-2" />
      <path d="M9 12h12l-3 -3" />
      <path d="M18 15l3 -3" />
    </Svg>
  );
}

export function BellIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M10 5a2 2 0 0 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6" />
      <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
    </Svg>
  );
}

// D-210 — the refunds history nav entry (Tabler `arrow-back-up`). A return arrow, not a receipt:
// Tabler's `receipt-refund` shares its outline with ReceiptIcon ("გადახდები"), and the sidebar's
// rule is one glyph per meaning. Money going BACK to the client is exactly what this arrow says.
export function ArrowBackUpIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 14l-4 -4l4 -4" />
      <path d="M5 10h11a4 4 0 1 1 0 8h-1" />
    </Svg>
  );
}

// D-218 — the payment-promises nav entry (Tabler `calendar-dollar`): money on a date, which is
// what a promise is. Not CalendarIcon (business calendar), CalendarStatsIcon (monthly commission)
// or CoinsIcon (advances) — one glyph per meaning.
export function CalendarDollarIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M13 21h-7a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v3" />
      <path d="M16 3v4" />
      <path d="M8 3v4" />
      <path d="M4 11h12.5" />
      <path d="M21 15h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3h-2.5" />
      <path d="M19 21v1m0 -8v1" />
    </Svg>
  );
}

// D-219 — the internal-tasks nav entry (Tabler `subtask`): an item with sub-items assigned from
// it. Not ListCheckIcon (commission rules), ClipboardCheckIcon (recovery plans) or BellIcon
// (alerts) — one glyph per meaning.
export function SubtaskIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M6 9l6 0" />
      <path d="M4 5l4 0" />
      <path d="M6 5v11a1 1 0 0 0 1 1h5" />
      <path d="M12 7m0 1a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-6a1 1 0 0 1 -1 -1z" />
      <path d="M12 15m0 1a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-6a1 1 0 0 1 -1 -1z" />
    </Svg>
  );
}

// D-223 — the Management Notebook nav entry (Tabler `notebook`): a sheet management annotates,
// which is exactly what the Notebook is. Not FileTextIcon (payment terms) or ClipboardListIcon
// (audit) — one glyph per meaning.
export function NotebookIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M6 4h11a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-11a1 1 0 0 1 -1 -1v-14a1 1 0 0 1 1 -1m3 0v18" />
      <path d="M13 8l2 0" />
      <path d="M13 12l2 0" />
    </Svg>
  );
}

// A month with figures in it — the monthly commission overview (K-M). Deliberately NOT
// CalendarIcon (business calendar) or ChartBarIcon (analytics): both already name other screens,
// and the sidebar's rule is one glyph per meaning.
export function CalendarStatsIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z" />
      <path d="M16 3v4M8 3v4M4 11h16" />
      <path d="M8 17v-2M12 17v-4M16 17v-1" />
    </Svg>
  );
}

// Recompute / run-again. Added for the commission backfill nav entry (K-D-UI): every existing
// icon in the sidebar already carries a different meaning, and reusing one would have made two
// unrelated screens look alike.
export function RefreshIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
      <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
    </Svg>
  );
}

// Platform administration nav entry (/admin/companies): provisioning companies, accounts and
// passwords. Every other nav icon is already taken — the Sidebar suite asserts each one is used
// exactly once, so a reused icon is a test failure, not a style nit.
export function KeyIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558a2 2 0 0 1 -1.239 .578l-.175 .008h-1.172a1 1 0 0 1 -.993 -.883l-.007 -.117v-1.172a2 2 0 0 1 .467 -1.284l.119 -.13l.414 -.414h2v-2h2v-2l2.144 -2.144l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0z" />
      <path d="M15 9h.01" />
    </Svg>
  );
}

// Password visibility pair (login form's show/hide toggle). Decorative like every other icon
// here — the toggle itself carries the accessible name, never the SVG.
export function EyeIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="2" />
      <path d="M22 12c-2.667 4.667 -5.667 7 -10 7s-7.333 -2.333 -10 -7c2.667 -4.667 5.667 -7 10 -7s7.333 2.333 10 7" />
    </Svg>
  );
}

export function EyeOffIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
      <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-4.333 0 -7.333 -2.333 -10 -7c1.407 -2.462 2.928 -4.272 4.564 -5.43m2.941 -1.204a9.61 9.61 0 0 1 2.495 -.366c4.333 0 7.333 2.333 10 7c-.778 1.362 -1.613 2.524 -2.503 3.487" />
      <line x1="3" y1="3" x2="21" y2="21" />
    </Svg>
  );
}
